// components/schedule/AvailableMembersPanel.tsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, IconButton,
  Chip, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import {
  AvailableMember,
  PREFERENCE_TYPES,
  PREFERENCE_TYPE_LABELS,
  PREFERENCE_TYPE_COLORS
} from '../../types/workPreference';
import { workPreferenceService } from '../../services/workPreference';

interface AvailableMembersPanelProps {
  member: AvailableMember; // 単一のメンバーを受け取るように変更
  date: Date;
  departmentType: 'equipment' | 'scaffolding';
  onClose: () => void;
  onSaved: () => void;
}


const AvailableMembersPanel: React.FC<AvailableMembersPanelProps> = ({
  member,
  date,
  departmentType,
  onClose,
  onSaved
}) => {
  // 状態変数を初期化
  const [preferenceType, setPreferenceType] = useState<number>(
    member.Preference_Type ?? PREFERENCE_TYPES.WANT_OFF
  );
  const [memo, setMemo] = useState<string>(member.Memo ?? '');
  const [loading, setLoading] = useState(false);

  // 勤務希望を設定
  const handleSetPreference = async () => {
    try {
      setLoading(true);
      const success = await workPreferenceService.setWorkPreference(
        member.ID,
        member.Department_ID,
        date,
        preferenceType,
        memo
      );

      if (success) {
        onSaved();
      }
    } catch (error) {
      console.error('Error setting work preference:', error);
    } finally {
      setLoading(false);
    }
  };

  // 勤務希望を削除
  const handleDeletePreference = async () => {
    if (member.Preference_Type === null || member.Preference_Type === undefined) return;

    try {
      setLoading(true);
      const success = await workPreferenceService.deleteWorkPreference(
        member.ID,
        member.Department_ID,
        date
      );

      if (success) {
        onSaved();
      }
    } catch (error) {
      console.error('Error deleting work preference:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        勤務希望登録
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" color="text.secondary">
          {member.LastName} {member.FirstName} - {format(date, 'yyyy/MM/dd(EEE)', { locale: ja })}
        </Typography>
        <Typography variant="subtitle2" gutterBottom>勤務希望</Typography>
        <RadioGroup
          value={preferenceType}
          onChange={(e) => setPreferenceType(Number(e.target.value))}
        >
          <FormControlLabel
            value={PREFERENCE_TYPES.MUST_WORK}
            control={<Radio />}
            label={PREFERENCE_TYPE_LABELS[PREFERENCE_TYPES.MUST_WORK]}
          />
          <FormControlLabel
            value={PREFERENCE_TYPES.PREFER_WORK}
            control={<Radio />}
            label={PREFERENCE_TYPE_LABELS[PREFERENCE_TYPES.PREFER_WORK]}
          />
          <FormControlLabel
            value={PREFERENCE_TYPES.WANT_OFF}
            control={<Radio />}
            label={PREFERENCE_TYPE_LABELS[PREFERENCE_TYPES.WANT_OFF]}
          />
        </RadioGroup>

        <TextField
          label="メモ"
          multiline
          rows={3}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
        />
      </DialogContent>
      <DialogActions>
        {member.Preference_Type !== null && member.Preference_Type !== undefined && (
          <Button onClick={handleDeletePreference} color="error" disabled={loading}>
            削除
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>キャンセル</Button>
        <Button
          onClick={handleSetPreference}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : '保存'}
        </Button>
      </DialogActions>
    </>
  );
};


export default AvailableMembersPanel;